import React from "react";

import type { Choices } from "@busbud/int-schemas/lib";
import type * as UDM from "@busbud/int-schemas/udm";

import { Bus } from "@app/components/bus/bus";
import { Drawer } from "@app/components/bus/drawer";
import ExpensiveSingleSeatBanner from "@app/components/bus/expensive-single-seat-banner";
import GenderRuleWarningBanner from "@app/components/bus/gender-rule-warning-banner";
import { SeatLegend } from "@app/components/bus/seat-legend";
import { createStyles } from "@app/components/tss/hooks/use-tss-styles";
import type { SeatSelectionDetailsMode } from "@app/constants/seat-selection-details-modes";
import { useAppContext } from "@app/helpers/hooks";
import { isReturn } from "@app/helpers/search";
import { createSeatSelectionValidator } from "@app/helpers/seat-selection-validator";
import type { SimplifiedBusLayout } from "@app/helpers/seat-selection-validator/seat-selection-gender-rules";
import type { Gender } from "@app/types/checkout-types";
import type { CurrencyCode } from "@app/types/currency";
import type { SearchInfo } from "@app/types/search-types";
import type {
  CreatePassenger,
  GetPassengerSeat,
  OnPassengerSelected,
  OnSeatSelected,
  PassengerSeatSelectionInstance,
  SeatInfo
} from "@app/types/seat-selection-types";

import type { SeatPrices } from "../seat-price";
import type {
  Direction,
  SelectedSeatsInfo
} from "../seat-selector/seat-selector";

import AmbiguousDepartureTimeAlert from "../../ambiguous-departure-time-alert";
import { SeatSelector } from "../seat-selector";
import { SummaryInline } from "../summary-inline";

const useStyles = createStyles(({ theme }) => {
  const { spacing } = theme.busbud;
  return {
    alert: {
      margin: spacing(1, 2)
    }
  };
});

interface Props {
  mode: SeatSelectionDetailsMode;
  currency: CurrencyCode;
  search: SearchInfo;
  is_external: boolean;
  trip_id: string;
  vehicle: UDM.Bus;
  prices: SeatPrices;
  departure_time?: string;
  segment_id: string;
  initial_passengers?: PassengerSeatSelectionInstance[];
  initial_selected_seats?: SelectedSeatsInfo; // TODO-CART-V2-SEAT-SELECTION: check if should remove the question mark
  is_checkout?: boolean; // TODO-CART-V2-SEAT-SELECTION: check if should remove the question mark
  onGenderSelect?: (gender: Gender, passenger_index: number) => void;
  onConfirm?: (
    passengers: PassengerSeatSelectionInstance[],
    trip_id: string
  ) => void;
}

export const SeatSelectorWithGender: React.FC<Props> = props => {
  const { features } = useAppContext();
  const validator = createSeatSelectionValidator(
    props.vehicle as SimplifiedBusLayout
  );

  const renderGenderRuleWarning = (
    passengers: PassengerSeatSelectionInstance[],
    getPassengerSeat: (
      passenger: PassengerSeatSelectionInstance
    ) => string | null
  ) => {
    const should_display_warning =
      isSelectionCompleted(passengers, getPassengerSeat) &&
      !isSeatGenderRulesStillValid(passengers, getPassengerSeat);

    if (!should_display_warning) {
      return null;
    }

    const invalid_gender: Choices.Gender | null = validator.getInvalidGender();
    if (!invalid_gender) {
      return null;
    }
    const gender_rules = validator.getMaxGenderRule(
      props.vehicle,
      invalid_gender
    );

    const existing_gendered_seats: string[] =
      validator.getGenderedSeatBeforeSelection(invalid_gender);

    return (
      <GenderRuleWarningBanner
        gender={invalid_gender}
        max_seats_rule={gender_rules}
        existing_gendered_seats={existing_gendered_seats}
      />
    );
  };

  const renderDepartureTimeWarning = (departure_time: string | undefined) => {
    return (
      <AmbiguousDepartureTimeAlert iso_departure_datetime={departure_time} />
    );
  };

  const renderLegend = (
    bus: UDM.Bus,
    passengers: PassengerSeatSelectionInstance[],
    getPassengerSeat: (
      passenger: PassengerSeatSelectionInstance
    ) => string | null
  ) => {
    if (features.RESULTS_SHOULD_HIDE_SEAT_SELECTION_LEGEND) {
      return <SeatLegend orientation="left" spacing={2} gendered={true} />;
    }

    return (
      <div className="flex flex-col gap-200">
        {/* In inline mode, we show the warning in the summary component */}
        {props.mode === "drawer" &&
          renderGenderRuleWarning(passengers, getPassengerSeat)}
        {props.mode === "drawer" &&
          renderDepartureTimeWarning(props.departure_time)}
        <ExpensiveSingleSeatBanner bus={bus} currency={props.currency} />
        <SeatLegend spacing={props.is_checkout ? 0.5 : 2} gendered={true} />
      </div>
    );
  };

  const renderSummary = (
    segment_id: string,
    seats_info: Record<string, SeatInfo>,
    passengers: PassengerSeatSelectionInstance[],
    getPassengerSeat: (
      passenger: PassengerSeatSelectionInstance
    ) => string | null,
    onConfirm: () => void
  ) => {
    const selected_seats = getSelectedSeatsList(
      seats_info,
      passengers,
      getPassengerSeat
    );

    const common_props = {
      currency: props.currency,
      prices: props.prices,
      segment_id,
      passengers,
      selected_seats,
      onConfirm
    };

    if (props.mode === "inline") {
      return (
        <SummaryInline
          {...common_props}
          is_external={props.is_external}
          gender_rule_warning={renderGenderRuleWarning(
            passengers,
            getPassengerSeat
          )}
        />
      );
    }

    if (
      features.RESULTS_SHOULD_SHOW_SEAT_SELECTOR_DRAWER ||
      props.is_checkout
    ) {
      const is_gender_rule_broken =
        isSelectionCompleted(passengers, getPassengerSeat) &&
        !isSeatGenderRulesStillValid(passengers, getPassengerSeat);

      return <Drawer {...common_props} disabled={is_gender_rule_broken} />;
    }

    return null;
  };

  const renderBus = (
    bus: UDM.Bus,
    other_assigned_seats_ids: (string | null)[],
    active_seat_id: string | null,
    _passengers: PassengerSeatSelectionInstance[],
    passenger_index: number,
    onPassengerSelected: OnPassengerSelected,
    onSeatSelected: OnSeatSelected,
    active_floor: number
  ) => {
    return (
      <Bus
        orientation={props.mode === "inline" ? "left" : "top"}
        key={`seat-selection-${props.trip_id}`}
        vehicle_driver={bus.driver}
        vehicle_layout={bus.layout}
        already_assigned_seat_ids={other_assigned_seats_ids}
        active_seat_id={active_seat_id}
        onSeatChange={seat => onSeatSelected(seat, { has_popover: true })}
        is_checkout={props.is_checkout}
        active_floor={active_floor}
        onGenderChange={gender => {
          if (props.onGenderSelect) {
            props.onGenderSelect(gender, passenger_index);
          }
          onPassengerSelected((passenger, createPassenger) =>
            setPassengerGender(gender, passenger, createPassenger)
          );
        }}
      />
    );
  };

  const setPassengerGender = (
    gender: Choices.Gender,
    passenger: PassengerSeatSelectionInstance,
    createPassenger: CreatePassenger
  ): PassengerSeatSelectionInstance => {
    const {
      passenger_category,
      outbound_selected_seat,
      return_selected_seat,
      questions,
      selected_seats
    } = passenger;

    const options = {
      passenger_category,
      outbound_selected_seat,
      return_selected_seat,
      questions,
      selected_seats
    };

    questions.gender = gender;

    return createPassenger(options);
  };

  const isSelectionCompleted = (
    passengers: PassengerSeatSelectionInstance[],
    getPassengerSeat: GetPassengerSeat
  ): boolean => {
    return passengers.every(passenger => {
      return !!(getPassengerSeat(passenger) && getPassengerGender(passenger));
    });
  };

  const isSeatGenderRulesStillValid = (
    passengers: PassengerSeatSelectionInstance[],
    getPassengerSeat: GetPassengerSeat
  ): boolean => {
    let assigned_gender_per_seat = {};
    assigned_gender_per_seat = passengers.reduce<
      Record<string, Choices.Gender | undefined>
    >((acc, passenger) => {
      const seat_id = getPassengerSeat(passenger);

      if (seat_id) {
        acc[seat_id] = getPassengerGender(passenger);
      }

      return acc;
    }, assigned_gender_per_seat);

    return validator.validateRules(assigned_gender_per_seat);
  };

  const getSelectedSeatsList = (
    seats_info: Record<string, SeatInfo>,
    passengers: PassengerSeatSelectionInstance[],
    getPassengerSeat: GetPassengerSeat
  ): UDM.SeatCell[] => {
    const seats: UDM.SeatCell[] = [];
    return passengers.reduce((seats, passenger) => {
      const seat_id = getPassengerSeat(passenger);
      const gender = getPassengerGender(passenger);

      if (seat_id && gender) {
        seats.push({
          ...seats_info[seat_id],
          type: "seat",
          id: seat_id,
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          display_name: seats_info[seat_id].display_name || seat_id,
          gender: getPassengerGender(passenger),
          availability: true,
          amenities: []
        });
      }

      return seats;
    }, seats);
  };

  const getPassengerGender = (
    passenger: PassengerSeatSelectionInstance
  ): Choices.Gender | undefined => {
    return passenger.questions.gender;
  };

  const direction: Direction = isReturn(props.search) ? "return" : "outbound";
  const { classes } = useStyles();
  return (
    <>
      <SeatSelector
        onConfirm={props.onConfirm}
        mode={props.mode}
        renderLegend={renderLegend}
        renderSummary={renderSummary}
        renderBus={renderBus}
        direction={direction}
        search={props.search}
        currency={props.currency}
        is_external={props.is_external}
        trip_id={props.trip_id}
        vehicle={props.vehicle}
        prices={props.prices}
        segment_id={props.segment_id}
        initial_passengers={props.initial_passengers}
        initial_selected_seats={props.initial_selected_seats}
        is_checkout={props.is_checkout}
      />
      {props.mode === "inline" && (
        <div className={classes.alert}>
          {renderDepartureTimeWarning(props.departure_time)}
        </div>
      )}
    </>
  );
};
